import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import logger from "../utils/logger";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useSearch, useSearchCallbacks } from "../contexts/SearchContext";
import HelpFooter from "../components/HelpFooter";
import { usePageTracking } from "../hooks/useAnalytics";
import useTwoFingerScroll from "../hooks/useTwoFingerScroll";
import AuthWall from "../components/AuthWall";
import IOSAppBanner from "../components/IOSAppBanner";
import StorySuggestionAlert from "../components/StorySuggestionAlert";
import ContinuePlaybackSection from "../components/ContinuePlaybackSection";
import { muxKnownOff } from "../lib/muxStreaming";
import { motion, useInView } from "framer-motion";
import { getOriginalsBranding } from "../config/originalsBranding";
import { getActiveSeasonalSpecials } from "../config/originalsSpecials";

/* ─── Pixley Movie — special player / badge treatment ─── */
const FEATURED_SHOW_ID = "6cd7a3a7-fbba-431a-bee4-b39fffd29c43";

/* Pixley Originals, newest → oldest (by when the show was created). The newest
   series leads the banner with the "New Show!" callout, whichever it is. */
const sortShowsNewestFirst = (showsList) => {
  if (!Array.isArray(showsList) || showsList.length === 0) return [];
  return [...showsList].sort((a, b) => {
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    return tb - ta;
  });
};

/* ─── animation presets ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

function AnimatedSection({ children, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── shared scrollable row ─── */
const ScrollRow = ({ children, className }) => {
  const ref = useRef(null);
  useTwoFingerScroll(ref);
  return (
    <div ref={ref} className={`sv-scroll-row ${className || ""}`}>
      {children}
    </div>
  );
};

/* ─── floating CTA for unauthenticated users ─── */
function FloatingCta({ onSignup }) {
  return (
    <div className="sv-floating-cta">
      <motion.button
        type="button"
        onClick={onSignup}
        className="sv-cta-pill"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -2 }}
        whileTap={{ y: 2 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0065F8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
        <span>Create your own story with Pixley</span>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
      </motion.button>
    </div>
  );
}

/* ── Hero banner — announcements, like the iOS Home banner ──
   One slide each: bright focused art, a solid badge, a short headline, at
   most one hook line, one button. Originals and new stories play; feature
   slides open the feature, or the app for what only the app does. */
const BANNER_ART = "/assets/banner";
const FEATURE_SLIDES = {
  hardcover: {
    id: "feature-hardcover", badge: "Hardcover books", icon: "menu_book", accent: "#FFB547",
    title: "Their story, a real book", hook: "Printed and shipped to your door.",
    cta: "Get the app", ctaIcon: "smartphone", img: `${BANNER_ART}/promo_hardcover.webp`, focus: "68% 22%",
    action: { type: "link", to: "/download" },
  },
  voices: {
    id: "feature-voices", badge: "New · Character voices", icon: "record_voice_over", accent: "#B89BFF",
    title: "Give them your voice", hook: "Record once. They talk in every story.",
    cta: "Make a character", ctaIcon: "mic", img: `${BANNER_ART}/promo_voices.webp`, focus: "72% 22%",
    action: { type: "link", to: "/character-lab" },
  },
  interactive: {
    id: "feature-interactive", badge: "Interactive stories", icon: "alt_route", accent: "#3BE0A0",
    title: "They choose what happens", hook: "Adventures that change with every pick.",
    cta: "Get the app", ctaIcon: "smartphone", img: `${BANNER_ART}/promo_interactive.webp`, focus: "55% 40%",
    action: { type: "link", to: "/download" },
  },
  shop: {
    id: "feature-shop", badge: "The Pixley Shop", icon: "shopping_bag", accent: "#FF7AB0",
    title: "Stickers & lunch bags", hook: "Made from their own characters.",
    cta: "Get the app", ctaIcon: "smartphone", img: `${BANNER_ART}/promo_shop.webp`, focus: "70% 22%",
    action: { type: "link", to: "/download" },
  },
};

/* A seasonal special (e.g. the Halloween episode) leads while its window is
   open; then the newest Original, hardcover books, then content and features
   alternate so two ads never play back to back (same order as iOS). */
const buildBanner = (shows, stories, getThumb) => {
  const originals = shows.slice(0, 2).map((show, i) => {
    const isMovie = show.id === FEATURED_SHOW_ID;
    const n = show.episode_count ?? 0;
    const brand = getOriginalsBranding(show);
    return {
      id: `original-${show.id}`, kind: "original",
      badge: isMovie ? "Pixley movie" : i === 0 ? "New series" : "Pixley Original",
      icon: isMovie ? "movie" : "auto_awesome", accent: "#FFC53D",
      title: show.name, hook: isMovie ? null : n > 0 ? `${n} episode${n === 1 ? "" : "s"}` : null,
      cta: "Watch", ctaIcon: "play_arrow", img: brand ? brand.art : getThumb(show), poster: show.cover_image_url, focus: "center 35%",
      logo: brand ? brand.logo : null,
      action: { type: "show", id: show.id },
    };
  });
  const storySlides = stories.filter((s) => s.thumbnail_url).slice(0, 2).map((story) => ({
    id: `story-${story.id}`, kind: "story", badge: "New story", icon: "auto_stories", accent: "#7CC4FF",
    title: story.name, hook: null, cta: "Watch", ctaIcon: "play_arrow", img: story.thumbnail_url, focus: "center 35%",
    action: { type: "story", id: story.id },
  }));
  const content = [...storySlides.slice(0, 1), ...originals.slice(1), ...storySlides.slice(1)];
  const features = [FEATURE_SLIDES.voices, FEATURE_SLIDES.interactive, FEATURE_SLIDES.shop];
  const lineup = getActiveSeasonalSpecials().map((sp) => ({
    ...sp, kind: "special", ctaIcon: "play_arrow",
    action: { type: "link", to: `/storyverse/shows/${sp.showId}?episode=${sp.episodeId}` },
  }));
  if (originals[0]) lineup.push(originals[0]); else if (content.length) lineup.push(content.shift());
  lineup.push(FEATURE_SLIDES.hardcover);
  while (content.length || features.length) {
    if (content.length) lineup.push(content.shift());
    if (features.length) lineup.push(features.shift());
  }
  return lineup;
};

const HERO_DWELL_MS = 7000;

const HeroCarousel = React.memo(({ items, onStoryClick, onShowClick, onLink }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { setIdx(0); }, [items]);
  useEffect(() => {
    if (items.length <= 1 || paused) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % items.length), HERO_DWELL_MS);
    return () => clearTimeout(t);
  }, [idx, items.length, paused]);

  if (!items.length) return null;
  const active = items[idx] || items[0];

  const go = (item) => {
    const a = item.action;
    if (a.type === "story") onStoryClick(a.id);
    else if (a.type === "show") onShowClick(a.id);
    else onLink(a.to);
  };

  return (
    <div className="sv-hero sv-banner" style={{ "--slide-accent": active.accent }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {items.map((item, i) => {
        const on = i === idx;
        const isVid = item.img?.includes(".mp4");
        return (
          <div key={item.id} className={`sv-hero-bg sv-banner-bg${on ? " on" : ""}`} aria-hidden={!on}>
            {isVid ? (
              <video src={on ? item.img : undefined} className="sv-hero-media" style={{ objectPosition: item.focus }}
                muted loop autoPlay playsInline preload="none" poster={item.poster || ""}
                onLoadedMetadata={(e) => { if (e.target.duration > 0) e.target.currentTime = e.target.duration / 2; }} />
            ) : (
              <img src={item.img} alt="" className="sv-hero-media" style={{ objectPosition: item.focus }}
                loading={i === 0 ? "eager" : "lazy"} onError={(e) => { e.target.src = "/assets/pixley-panda-nobg.png"; }} />
            )}
          </div>
        );
      })}
      <div className="sv-hero-bottom sv-banner-copy">
        {/* Re-keyed per slide so the CSS entrance replays; no JS animation
            clock, so the words show even if the tab was in the background. */}
        <div key={active.id}>
          <span className="sv-banner-in sv-banner-badge" style={{ animationDelay: "0ms" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>{active.icon}</span>
            {active.badge}
          </span>
          {active.logo ? (
            <h1 className="sv-banner-in sv-hero-title sv-hero-logo" style={{ animationDelay: "70ms" }}>
              <img src={active.logo} alt={active.title} />
            </h1>
          ) : (
            <h1 className="sv-banner-in sv-hero-title sv-banner-title" style={{ animationDelay: "70ms" }}>{active.title}</h1>
          )}
          {active.hook && <p className="sv-banner-in sv-banner-hook" style={{ animationDelay: "140ms" }}>{active.hook}</p>}
          <button className="sv-banner-in sv-hero-play sv-banner-cta" style={{ animationDelay: "210ms" }} onClick={() => go(active)}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, fontVariationSettings: "'FILL' 1" }}>{active.ctaIcon}</span>
            {active.cta}
          </button>
        </div>
      </div>
      {items.length > 1 && (
        <div className="sv-banner-progress" role="tablist" aria-label="Banner slides">
          {items.map((item, i) => (
            <button key={item.id} role="tab" aria-selected={i === idx} aria-label={`${item.badge}: ${item.title}`}
              className={`sv-banner-bar${i === idx ? " on" : ""}${i < idx ? " done" : ""}`} onClick={() => setIdx(i)}>
              <span style={i === idx && !paused ? { animationDuration: `${HERO_DWELL_MS}ms` } : undefined}
                className={i === idx && !paused ? "run" : ""} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
const Storyverse = () => {
  usePageTracking("Storyverse");
  const { apiClient } = useAPI();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { searchQuery, isSearching } = useSearch();
  const { registerSearchCallbacks, unregisterSearchCallbacks } = useSearchCallbacks();

  const [shows, setShows] = useState([]);
  const [stories, setStories] = useState([]);
  const [publicStorybooks, setPublicStorybooks] = useState([]);
  const [searchStorybooks, setSearchStorybooks] = useState([]);
  // True when the backend found no real match and sent the closest rows instead.
  const [searchSuggested, setSearchSuggested] = useState(false);
  const [searchReady, setSearchReady] = useState(false);
  const [originalStories, setOriginalStories] = useState([]);
  const [forYouStories, setForYouStories] = useState([]);
  const [, setForYouStorybooks] = useState([]);
  const [forYouLoading, setForYouLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuthWall, setShowAuthWall] = useState(false);
  const [authWallFeature, setAuthWallFeature] = useState("content");
  const [showSuggestionAlert, setShowSuggestionAlert] = useState(false);
  const [pixleyMixes, setPixleyMixes] = useState({ top_views: [], adventure: [], mystery: [], my_episodes: [] });
  const [mixesLoading, setMixesLoading] = useState(false);

  /* ── data loading ── */
  const origRef = useRef(originalStories);
  const apiRef = useRef(apiClient);
  const queryRef = useRef(searchQuery);
  origRef.current = originalStories;
  queryRef.current = searchQuery;
  apiRef.current = apiClient;

  // Tier 1: Core content — fast APIs, blocks render
  const loadStoriverseData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [showsR, storiesR, booksR] = await Promise.all([
        apiClient.getStoriverseShows(),
        apiClient.getStoriverseStories(),
        apiClient.getPublicStorybooksFeed(10, 0),
      ]);
      if (!showsR.status) throw new Error(showsR.error || "Failed to load");
      setShows(sortShowsNewestFirst(showsR.data || []));
      if (storiesR.status) { const d = storiesR.data || []; setOriginalStories(d); origRef.current = d; if (!queryRef.current) setStories(d); }
      if (booksR?.status) { setPublicStorybooks((Array.isArray(booksR.data) ? booksR.data : []).filter((b) => b?.is_public)); } else setPublicStorybooks([]);
    } catch (err) { logger.error("loadStoriverseData error:", err); setError(err.message); setPublicStorybooks([]); }
    finally { setLoading(false); }
  }, [apiClient]);

  // Tier 2: Secondary content — loads after page is visible, fills in when ready
  useEffect(() => {
    if (!apiClient || loading) return;
    // Mixes
    setMixesLoading(true);
    apiClient.getPixleyMixes().then((response) => {
      const emptyMixes = { top_views: [], adventure: [], mystery: [], my_episodes: [] };
      const raw = response?.data ?? response;
      const keys = ["top_views", "adventure", "mystery", "my_episodes"];
      let out = { ...emptyMixes };
      if (Array.isArray(raw) && raw.length) {
        raw.forEach((item) => { if (item?.key && keys.includes(item.key) && Array.isArray(item.value)) out[item.key] = item.value; });
      } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        keys.forEach((k) => { out[k] = Array.isArray(raw[k]) ? raw[k] : []; });
      }
      const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };
      out.adventure = shuffle(out.adventure);
      out.mystery = shuffle(out.mystery);
      setPixleyMixes(out);
    }).catch((err) => logger.error("Pixley Mixes error:", err))
      .finally(() => setMixesLoading(false));
    // For You
    if (isAuthenticated) {
      setForYouLoading(true);
      apiClient.getForYouStories().then((r) => {
        if (r?.status && r.data) { setForYouStories(r.data.stories || []); setForYouStorybooks(r.data.storybooks || []); }
      }).catch(() => {}).finally(() => setForYouLoading(false));
    }
  }, [apiClient, loading, isAuthenticated]);

  useEffect(() => { loadStoriverseData(); }, [loadStoriverseData]);
  useEffect(() => { const s = sessionStorage.getItem("showStorySuggestion"); if (s === "true") { setShowSuggestionAlert(true); sessionStorage.removeItem("showStorySuggestion"); } }, []);

  // While the query is changing / request is in flight, show search skeletons
  // instead of a premature empty state or stale rows.
  useEffect(() => {
    if (!searchQuery?.trim()) {
      setSearchReady(false);
      return;
    }
    setSearchReady(false);
  }, [searchQuery]);

  useEffect(() => {
    registerSearchCallbacks(
      async (q) => {
        if (!q?.trim()) {
          setStories(origRef.current);
          setSearchStorybooks([]);
          setSearchSuggested(false);
          setSearchReady(false);
          return;
        }
        try {
          const r = await apiRef.current.searchStoriesByEmbeddings(q, 10);
          // Ignore stale responses if the user kept typing.
          if (queryRef.current.trim() !== String(q).trim()) return;

          const ok = r?.status === true || r?.status === "success" || r?.success === true;
          if (!ok) {
            setStories([]);
            setSearchStorybooks([]);
            setSearchSuggested(false);
            setSearchReady(true);
            return;
          }

          // Backend may return top-level arrays and/or nested/mixed shapes.
          let nextStories = Array.isArray(r.stories)
            ? r.stories
            : Array.isArray(r?.data?.stories)
              ? r.data.stories
              : [];
          let nextStorybooks = Array.isArray(r.storybooks)
            ? r.storybooks
            : Array.isArray(r?.data?.storybooks)
              ? r.data.storybooks
              : [];

          if (Array.isArray(r?.data) && r.data.length) {
            const fromMixedStories = [];
            const fromMixedBooks = [];
            r.data.forEach((item) => {
              const row = item?.data || item;
              if (!row?.id) return;
              if (item?.type === "storybook") fromMixedBooks.push(row);
              else if (item?.type === "story") fromMixedStories.push(row);
            });
            if (!nextStories.length) nextStories = fromMixedStories;
            if (!nextStorybooks.length) nextStorybooks = fromMixedBooks;
          }

          // Normalize cover fields so book cards always have a usable image.
          nextStorybooks = nextStorybooks.map((b) => ({
            ...b,
            thumbnail_url:
              b.thumbnail_url || b.cover_url || b.cover_image_url || null,
          }));

          if (queryRef.current.trim() !== String(q).trim()) return;

          setStories(nextStories);
          setSearchStorybooks(nextStorybooks);
          // Nothing matched, so these are the closest things the library has.
          // Saying so is the point: an unlabelled near-miss reads as a wrong answer.
          setSearchSuggested(r.result_kind === "suggestion");
        } catch (e) {
          logger.error("Search error:", e);
          if (queryRef.current.trim() !== String(q).trim()) return;
          setStories([]);
          setSearchStorybooks([]);
          setSearchSuggested(false);
        } finally {
          if (queryRef.current.trim() === String(q).trim()) {
            setSearchReady(true);
          }
        }
      },
      () => {
        setStories(origRef.current);
        setSearchStorybooks([]);
        setSearchSuggested(false);
        setSearchReady(false);
      }
    );
    return () => unregisterSearchCallbacks();
  }, [registerSearchCallbacks, unregisterSearchCallbacks]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── actions ── */
  const handleShowClick = (id) => {
    // Guests browse freely: ShowDetail gives everyone the first 2 episodes
    // and walls the rest, so the feed no longer blocks the click itself.
    // Featured show goes straight to player (no episode list)
    if (id === FEATURED_SHOW_ID) {
      navigate(`/storyverse/standalone/${id}`);
    } else {
      navigate(`/storyverse/shows/${id}`);
    }
  };
  const handleStoryClick = async (id, shareToken = null) => {
    if (window.gtag) window.gtag("event", "episode_view", { story_id: id });
    try { await apiClient.incrementStoryView(id); } catch (_) {}
    sessionStorage.setItem("autoplay_video_story", "true");
    // Public feed stories are token-gated now — carry the share_token so the
    // detail page can open a story the viewer doesn't own.
    navigate(`/stories/${id}${shareToken ? `?token=${encodeURIComponent(shareToken)}` : ""}`);
  };
  const getThumb = (show) => {
    if (!show.episodes?.length) return show.cover_image_url;
    const ep = show.episodes.find((e) => e.file_url && e.status === "GENERATED");
    return ep ? ep.file_url : show.cover_image_url;
  };

  /* ── hero banner (memoized, passed to the isolated Hero component) ── */
  const heroItems = useMemo(
    () => buildBanner(shows, originalStories, getThumb),
    // getThumb is a plain function of the show; the lineup only changes with the data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shows, originalStories]
  );

  /* ─────────────────────────────────────────────
     SUB-COMPONENTS
     ───────────────────────────────────────────── */

  // Global flag: only enable hover videos after images have had time to load
  const [videosEnabled, setVideosEnabled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVideosEnabled(true), 5000); // 5s after mount
    return () => clearTimeout(t);
  }, []);

  // Short animated previews (Mux) for story cards: a few KB of WebP on hover
  // instead of starting the whole episode MP4. Stories without one keep the
  // video hover.
  const [previewUrls, setPreviewUrls] = useState({});
  const previewIdsKey = stories.slice(0, 40).map((s) => s.id).join(",");
  const previewShowIdsKey = shows.slice(0, 40).map((s) => s.id).join(",");
  useEffect(() => {
    if (!videosEnabled || !(previewIdsKey || previewShowIdsKey) || !apiClient || muxKnownOff()) return undefined;
    let cancelled = false;
    const body = {
      ids: previewIdsKey ? previewIdsKey.split(",") : [],
      show_ids: previewShowIdsKey ? previewShowIdsKey.split(",") : [],
    };
    apiClient
      .request("/mux/previews", { method: "POST", body: JSON.stringify(body) })
      .then((res) => {
        // Only re-render (which remounts every card) when there's something new.
        if (!cancelled && res?.data && typeof res.data === "object" && Object.keys(res.data).length) {
          setPreviewUrls((prev) => ({ ...prev, ...res.data }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [videosEnabled, previewIdsKey, previewShowIdsKey, apiClient]);

  /* ── Card (used for stories, storybooks, shows) ── */
  const Card = ({ thumb, title, meta, onClick, videoUrl, previewUrl, index = 0, badge, eager = false, hideTitle = false }) => {
    const [hovered, setHovered] = useState(false);
    const vRef = useRef(null);
    useEffect(() => {
      if (!vRef.current || !videosEnabled) return;
      if (hovered) vRef.current.play().catch(() => {});
      else { vRef.current.pause(); vRef.current.currentTime = 0; }
    }, [hovered, videosEnabled]);

    const gradients = [
      "linear-gradient(135deg,#0065F8,#00CAFF)", "linear-gradient(135deg,#7FC6FF,#E1EBFF)",
      "linear-gradient(135deg,#3EDC8F,#00CAFF)", "linear-gradient(135deg,#FFD76A,#FF6B9A)",
      "linear-gradient(135deg,#0065F8,#7FC6FF)", "linear-gradient(135deg,#00CAFF,#E6F7EE)",
    ];
    const hash = (title || "").split("").reduce((a, c) => { a = (a << 5) - a + c.charCodeAt(0); return a & a; }, 0);
    const fallback = gradients[Math.abs(hash) % gradients.length];

    return (
      <motion.article className="sv-card" variants={fadeUp} custom={index} onClick={onClick}
        onMouseEnter={() => videosEnabled && setHovered(true)} onMouseLeave={() => setHovered(false)}
        whileHover={{ y: -5 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
        <div className="sv-card-thumb">
          {thumb ? (
            <img src={thumb} alt={title} className="sv-card-img"
              loading={eager ? "eager" : "lazy"}
              style={{ opacity: 0, transition: "opacity 0.3s ease" }}
              onLoad={(e) => { e.target.style.opacity = 1; }}
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="sv-card-placeholder" style={{ background: fallback }}>
              {(title || "").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
            </div>
          )}
          {previewUrl && videosEnabled ? (
            hovered && <img src={previewUrl} alt="" aria-hidden="true" className="sv-card-vid" style={{ objectFit: "cover" }} />
          ) : videoUrl && videosEnabled && (
            <video ref={vRef} src={videoUrl} className="sv-card-vid" muted loop playsInline preload="none"
              style={{ opacity: hovered ? 1 : 0 }} />
          )}
          {badge && <div className="sv-badge" style={{ background: "none", padding: 0 }}>{badge}</div>}
          <div className={`sv-card-hover-overlay${hovered ? " show" : ""}`}>
            <div className="sv-card-play-circle">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        </div>
        <div className="sv-card-body">
          <h3 className={`sv-card-name${hideTitle ? " sv-sr-only" : ""}`}>{title}</h3>
          <span className="sv-card-sub" style={meta ? undefined : { visibility: "hidden" }}>{meta || "\u00A0"}</span>
        </div>
      </motion.article>
    );
  };

  /* ── Section wrapper ── */
  const Section = ({ title, children }) => (
    <AnimatedSection className="sv-section">
      <motion.h2 className="sv-section-heading" variants={fadeUp}>
        {title}
      </motion.h2>
      {children}
    </AnimatedSection>
  );

  /* ── Horizontal row ── */
  const HorizontalRow = ({ title, items, renderCard, variableWidth, book }) => {
    if (!items.length) return null;

    const getSlotClass = (item) => {
      if (book) return "sv-book-slot";
      if (!variableWidth) return "sv-card-slot";
      const maxViews = Math.max(...items.map((s) => s.views || 0), 1);
      const ratio = (item.views || 0) / maxViews;
      return ratio >= 0.4 ? "sv-card-slot wide" : "sv-card-slot";
    };

    return (
      <Section title={title}>
        <ScrollRow>
          {items.map((item, i) => (
            <div key={item.id} className={getSlotClass(item)}>
              {renderCard(item, i)}
            </div>
          ))}
        </ScrollRow>
      </Section>
    );
  };

  const SkeletonRow = ({ book = false, cards = 6 }) => (
    <div className="sv-section" aria-hidden="true">
      <div className="sv-skel sv-skel-heading" />
      <div className="sv-scroll-row">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className={book ? "sv-book-slot" : "sv-card-slot"}>
            <div className={`sv-skel ${book ? "sv-skel-book" : "sv-skel-card"}`} />
          </div>
        ))}
      </div>
    </div>
  );

  const storyCard = (s, i, eager = false) => (
    <Card thumb={s.thumbnail_url} title={s.name} meta={`${s.views || 0} views`}
      onClick={() => handleStoryClick(s.id, s.share_token)} videoUrl={s.video_url || s.file_url}
      previewUrl={previewUrls[s.id]} index={i} eager={eager} />
  );
  const eagerStoryCard = (s, i) => storyCard(s, i, true);

  const showCard = (s, i) => {
    const isFeatured = s.id === FEATURED_SHOW_ID;
    const brand = getOriginalsBranding(s);
    const premiumBadge = !isAuthenticated ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, background: "rgba(0,101,248,0.85)", color: "#fff", fontSize: 11, fontWeight: 700, boxShadow: "0px 2px 0px rgba(11,26,51,0.15)" }}><svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg> Premium</span> : null;
    const movieBadge = isFeatured ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, background: "#0065F8", color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", boxShadow: "0px 2px 0px #073B8E" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}>movie</span>
        Pixley Movie
      </span>
    ) : null;
    return (
      <Card thumb={brand ? brand.cover : getThumb(s)} title={s.name} meta={isFeatured ? "Pixley Movie ✦" : `${s.episode_count ?? 0} episodes`}
        onClick={() => handleShowClick(s.id)} index={i} hideTitle={!!brand} previewUrl={previewUrls[s.id]}
        badge={movieBadge || premiumBadge} />
    );
  };

  const bookCard = (b, i) => {
    const title = b.title || "Untitled";
    const thumb = b.thumbnail_url || b.cover_url || b.cover_image_url || null;
    const SPINE_COLORS = ["#E0567A", "#5B8DEF", "#3FB984", "#F2A33C", "#9B6BE0", "#E86F51", "#22B6C4", "#F25C9A"];
    const h = title.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
    const accent = SPINE_COLORS[Math.abs(h) % SPINE_COLORS.length];
    return (
      <motion.article
        className="sv-book"
        style={{ "--sv-accent": accent }}
        variants={fadeUp}
        custom={i}
        onClick={() => navigate(`/storybooks/${b.id}`)}
        whileHover={{ y: -6 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="sv-book-stage">
          <div className="sv-book-3d">
            {/* right fore-edge + bottom edge = the book's page thickness (true 3D faces) */}
            <div className="sv-book-edge sv-book-edge--right" aria-hidden="true" />
            <div className="sv-book-edge sv-book-edge--bottom" aria-hidden="true" />
            {/* front cover */}
            <div className="sv-book-cover">
              {thumb ? (
                <img
                  src={thumb}
                  alt={title}
                  className="sv-book-img"
                  loading="lazy"
                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                />
              ) : (
                <div className="sv-book-img sv-book-img--ph" />
              )}
              <span className="sv-book-spine" aria-hidden="true" />
              <span className="sv-book-scrim" aria-hidden="true" />
              <h3 className="sv-book-title">{title}</h3>
            </div>
          </div>
        </div>
        <div className="sv-book-meta">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.5C10.5 5.5 8 5 4 5v12c4 0 6.5.5 8 1.5 1.5-1 4-1.5 8-1.5V5c-4 0-6.5.5-8 1.5zM12 6.5V18.5" />
          </svg>
          <span>{b.views ?? 0} views</span>
        </div>
      </motion.article>
    );
  };

  /* ── error only — no loading gate, page renders immediately ── */

  if (error) return (
    <div className="sv-container"><div className="sv-center-screen">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: "1.4rem", marginBottom: 8, color: "#0B1A33" }}>Something went wrong</h1>
        <p style={{ color: "#334155", marginBottom: 20 }}>We couldn't load your content right now.</p>
        <button className="sv-retry" onClick={loadStoriverseData}>Try Again</button>
      </motion.div>
    </div></div>
  );

  const popular = pixleyMixes.top_views || [];
  const adventure = pixleyMixes.adventure || [];
  const mystery = pixleyMixes.mystery || [];
  const mixesReady = !loading && !mixesLoading;

  return (
    <div className="sv-container">
      {!isAuthenticated && <FloatingCta onSignup={() => navigate("/auth?mode=signup")} />}

      {/* Hero — show a skeleton in its place until core content arrives */}
      {heroItems.length > 0 ? (
        <HeroCarousel items={heroItems} onStoryClick={handleStoryClick} onShowClick={handleShowClick} onLink={navigate} />
      ) : (
        <div className="sv-hero-skel sv-skel" aria-hidden="true" />
      )}

      <div className="sv-body">
        {/* Search results take priority at the top when searching */}
        {searchQuery && (
          <>
            {(isSearching || !searchReady) ? (
              <>
                <SkeletonRow cards={6} />
                <SkeletonRow book cards={6} />
              </>
            ) : (
              <>
                <HorizontalRow
                  title={searchSuggested ? "Stories You Might Like" : "Stories"}
                  items={stories.slice(0, 10)}
                  renderCard={storyCard}
                />
                <HorizontalRow
                  title={searchSuggested ? "Storybooks You Might Like" : "Storybooks"}
                  items={searchStorybooks.slice(0, 10)}
                  renderCard={bookCard}
                  book
                />
                {stories.length === 0 && searchStorybooks.length === 0 && (
                  <Section title="Search Results">
                    <motion.div className="sv-empty-msg" variants={fadeUp}>
                      <p>Nothing found matching your search.</p>
                      <span>Try different keywords</span>
                    </motion.div>
                  </Section>
                )}
              </>
            )}
          </>
        )}

        {/* Continue watching */}
        {isAuthenticated && <ContinuePlaybackSection apiClient={apiClient} />}

        {/* Pixley Originals */}
        {shows.length > 0 ? (
          <HorizontalRow title="Pixley Originals" items={shows} renderCard={showCard} />
        ) : (loading && !searchQuery) ? (
          <SkeletonRow />
        ) : null}

        {/* Normal feed (hidden while searching). Each section shows a skeleton in its
            own position until its data lands, so the page fills top-to-bottom in place
            instead of bottom-first (Tier-1 rows used to beat the Tier-2 rows above them). */}
        {!searchQuery && (
          <>
            {/* For You */}
            {forYouStories.length > 0 ? (
              <HorizontalRow title="For You" items={forYouStories} renderCard={storyCard} />
            ) : (isAuthenticated && forYouLoading) ? (
              <SkeletonRow />
            ) : null}

            {/* Trending */}
            {popular.length > 0 ? (
              <HorizontalRow title="Trending Now" items={popular} renderCard={eagerStoryCard} variableWidth />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}

            {/* Adventure */}
            {adventure.length > 0 ? (
              <HorizontalRow title="Adventure Stories" items={adventure} renderCard={storyCard} variableWidth />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}

            {/* Mystery */}
            {mystery.length > 0 ? (
              <HorizontalRow title="Mystery Stories" items={mystery} renderCard={storyCard} />
            ) : !mixesReady ? (
              <SkeletonRow />
            ) : null}
          </>
        )}

        {/* Public storybooks — hidden while searching so it cannot sit under an
            identically titled row of search results. */}
        {searchQuery ? null : publicStorybooks.length > 0 ? (
          <HorizontalRow title="Storybooks" items={publicStorybooks} renderCard={bookCard} book />
        ) : loading ? (
          <SkeletonRow book />
        ) : null}
      </div>

      <AuthWall isOpen={showAuthWall} onClose={() => setShowAuthWall(false)} feature={authWallFeature} />
      <IOSAppBanner />
      {showSuggestionAlert && <StorySuggestionAlert onClose={() => setShowSuggestionAlert(false)} />}
      <HelpFooter />
    </div>
  );
};

export default Storyverse;
